/**
 * Atlas — Professional Profile (public + own).
 * Themed against the Signal / Navy design tokens (see src/styles/tokens.css).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { marketplaceAPI } from '../services/api';

// ── Helpers ────────────────────────────────────────────────────────────────────
function Stars({ rating, size = 14, interactive = false, onRate }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2, lineHeight: 1 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          onClick={() => interactive && onRate && onRate(n)}
          style={{
            fontSize: size,
            color: n <= Math.round(rating) ? 'var(--pa-caution)' : 'var(--pa-slate-4)',
            cursor: interactive ? 'pointer' : 'default',
          }}
        >
          ★
        </span>
      ))}
    </span>
  );
}

function Badge({ text, tone = 'neutral' }) {
  const tones = {
    neutral: { bg: 'var(--pa-tint)',        color: 'var(--pa-navy)' },
    brand:   { bg: 'var(--pa-navy)',        color: 'var(--pa-paper)' },
    success: { bg: 'var(--pa-positive-bg)', color: 'var(--pa-positive)' },
    caution: { bg: 'var(--pa-caution-bg)',  color: 'var(--pa-caution)' },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 'var(--pa-radius-pill)',
      fontSize: 11,
      fontWeight: 600,
      background: t.bg,
      color: t.color,
      letterSpacing: '0.04em',
    }}>
      {text}
    </span>
  );
}

// ── Review Modal ───────────────────────────────────────────────────────────────
function ReviewModal({ proId, onClose, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function submit() {
    if (!rating) { setError('Please select a star rating'); return; }
    setSaving(true);
    setError('');
    try {
      await marketplaceAPI.createReview(proId, { rating, body: body.trim() || undefined });
      onSubmitted();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not submit review');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-head">
          <div>
            <h3 className="serif" style={{ margin: 0, fontSize: 18, color: 'var(--pa-navy)', fontWeight: 600 }}>
              Write a review
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--pa-text-muted)' }}>
              Share your experience with this professional.
            </p>
          </div>
          <button className="btn ghost sm" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--pa-text-muted)', marginBottom: 8 }}>
              Rating *
            </div>
            <Stars rating={rating} size={28} interactive onRate={setRating} />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--pa-text-muted)', marginBottom: 8 }}>
              Review (optional)
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Describe the quality of work, communication, reliability…"
              maxLength={1000}
              style={{
                width: '100%', boxSizing: 'border-box', minHeight: 100,
                padding: '8px 12px', borderRadius: 'var(--pa-radius-md)',
                border: '1px solid var(--pa-border)',
                fontSize: 13, resize: 'vertical', fontFamily: 'inherit',
                background: 'var(--pa-bg-elevated)',
                color: 'var(--pa-text)',
              }}
            />
            <div style={{ fontSize: 11, color: 'var(--pa-text-subtle)', textAlign: 'right', marginTop: 4 }}>
              {body.length}/1000
            </div>
          </div>

          {error && (
            <div style={{
              padding: '8px 12px',
              background: 'var(--pa-negative-bg)',
              border: '1px solid var(--pa-negative)',
              borderRadius: 'var(--pa-radius-md)',
              fontSize: 13,
              color: 'var(--pa-negative)',
            }}>
              {error}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={saving || !rating}>
            {saving ? 'Submitting…' : 'Submit review'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Profile Form ─────────────────────────────────────────────────────────
function EditProfileForm({ profile, onSave, onCancel }) {
  const [form, setForm] = useState({
    bio: profile.bio || '',
    secondary_trade: profile.secondary_trade || '',
    license_number: profile.license_number || '',
    suburb: profile.suburb || '',
    state: profile.state || '',
    service_areas: (profile.service_areas || []).join(', '),
    response_time: profile.response_time || '',
    rate_band: profile.rate_band || '',
    is_available: profile.is_available ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set(key, value) { setForm((f) => ({ ...f, [key]: value })); }

  async function save() {
    setSaving(true);
    setError('');
    try {
      const payload = {
        bio: form.bio.trim() || undefined,
        secondary_trade: form.secondary_trade.trim() || undefined,
        license_number: form.license_number.trim() || undefined,
        suburb: form.suburb.trim() || undefined,
        state: form.state.trim() || undefined,
        service_areas: form.service_areas.split(',').map((s) => s.trim()).filter(Boolean),
        response_time: form.response_time.trim() || undefined,
        rate_band: form.rate_band || undefined,
        is_available: form.is_available,
      };
      const { data } = await marketplaceAPI.updateMyProfile(payload);
      onSave(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save profile');
    } finally {
      setSaving(false);
    }
  }

  const fieldStyle = {
    width: '100%', boxSizing: 'border-box', padding: '8px 12px',
    borderRadius: 'var(--pa-radius-md)',
    border: '1px solid var(--pa-border)',
    background: 'var(--pa-bg-elevated)',
    color: 'var(--pa-text)',
    fontSize: 13, fontFamily: 'inherit',
  };
  const label = (text) => (
    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--pa-text-muted)', marginBottom: 6 }}>
      {text}
    </div>
  );

  return (
    <div className="panel" style={{ padding: 24, marginTop: 20 }}>
      <h4 className="serif" style={{ margin: '0 0 20px', fontSize: 16, color: 'var(--pa-navy)', fontWeight: 600 }}>
        Edit profile
      </h4>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          {label('Bio')}
          <textarea
            value={form.bio}
            onChange={(e) => set('bio', e.target.value)}
            placeholder="Describe your expertise, years of experience, approach…"
            style={{ ...fieldStyle, minHeight: 90, resize: 'vertical' }}
          />
        </div>
        <div>
          {label('Secondary trade')}
          <input style={fieldStyle} value={form.secondary_trade} onChange={(e) => set('secondary_trade', e.target.value)} placeholder="e.g. Tiling" />
        </div>
        <div>
          {label('Licence / Registration #')}
          <input style={fieldStyle} value={form.license_number} onChange={(e) => set('license_number', e.target.value)} placeholder="e.g. 123456C" />
        </div>
        <div>
          {label('Suburb')}
          <input style={fieldStyle} value={form.suburb} onChange={(e) => set('suburb', e.target.value)} placeholder="e.g. Bondi" />
        </div>
        <div>
          {label('State')}
          <input style={fieldStyle} value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="e.g. NSW" />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          {label('Service areas (comma-separated)')}
          <input style={fieldStyle} value={form.service_areas} onChange={(e) => set('service_areas', e.target.value)} placeholder="e.g. Bondi, Surry Hills, Newtown" />
        </div>
        <div>
          {label('Response time')}
          <input style={fieldStyle} value={form.response_time} onChange={(e) => set('response_time', e.target.value)} placeholder="e.g. within 2 hours" />
        </div>
        <div>
          {label('Rate band')}
          <select style={fieldStyle} value={form.rate_band} onChange={(e) => set('rate_band', e.target.value)}>
            <option value="">— not set —</option>
            <option value="$">$ Budget-friendly</option>
            <option value="$$">$$ Mid-range</option>
            <option value="$$$">$$$ Premium</option>
          </select>
        </div>
        <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            type="checkbox"
            id="available-toggle"
            checked={form.is_available}
            onChange={(e) => set('is_available', e.target.checked)}
            style={{ width: 16, height: 16, accentColor: 'var(--pa-signal)' }}
          />
          <label htmlFor="available-toggle" style={{ fontSize: 13, color: 'var(--pa-text-muted)', cursor: 'pointer' }}>
            Available for new jobs
          </label>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '8px 12px',
          background: 'var(--pa-negative-bg)',
          border: '1px solid var(--pa-negative)',
          borderRadius: 'var(--pa-radius-md)',
          fontSize: 13,
          color: 'var(--pa-negative)',
          marginTop: 16,
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
        <button className="btn ghost" onClick={onCancel}>Cancel</button>
        <button className="btn primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ── Job Card ──────────────────────────────────────────────────────────────────
function JobCard({ job, isOwn, onVisibilityChange }) {
  const [updating, setUpdating] = useState(false);

  async function toggle(field) {
    setUpdating(true);
    try {
      const { data } = await marketplaceAPI.updateJobVisibility(job.id, { [field]: !job[field] });
      onVisibilityChange(data);
    } finally {
      setUpdating(false);
    }
  }

  const masked = job.mask_details && !isOwn;
  const dimmed = !isOwn && !job.show_on_profile;

  return (
    <div className="panel" style={{
      padding: 18,
      background: dimmed ? 'var(--pa-paper-warm)' : 'var(--pa-bg-elevated)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <Badge text={job.trade} tone="success" />
            {job.completed_date && (
              <span style={{ fontSize: 12, color: 'var(--pa-text-subtle)' }}>
                {new Date(job.completed_date).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: 'var(--pa-text-muted)', lineHeight: 1.5, marginBottom: 6 }}>
            {masked
              ? <span style={{ color: 'var(--pa-text-subtle)', fontStyle: 'italic' }}>Details hidden at pro's request</span>
              : job.description.length > 120 ? job.description.slice(0, 120) + '…' : job.description
            }
          </div>
          <div style={{ fontSize: 12, color: 'var(--pa-text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {job.property_location && (
              <span>📍 {masked ? job.property_location : (job.property_address || job.property_location)}</span>
            )}
            {job.final_amount && (
              <span style={{ color: 'var(--pa-text)' }}>
                ${job.final_amount.toLocaleString()}
              </span>
            )}
          </div>
        </div>

        {isOwn && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--pa-text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={job.show_on_profile}
                onChange={() => toggle('show_on_profile')}
                disabled={updating}
                style={{ accentColor: 'var(--pa-signal)' }}
              />
              Show on profile
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--pa-text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={job.mask_details}
                onChange={() => toggle('mask_details')}
                disabled={updating}
                style={{ accentColor: 'var(--pa-signal)' }}
              />
              Mask address & client
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Review Card ───────────────────────────────────────────────────────────────
function ReviewCard({ review }) {
  return (
    <div className="panel" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'var(--pa-tint)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 700, color: 'var(--pa-navy)', flexShrink: 0,
        }}>
          {review.reviewer_name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pa-text)' }}>{review.reviewer_name}</div>
          <div style={{ fontSize: 11, color: 'var(--pa-text-subtle)' }}>
            {new Date(review.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Stars rating={review.rating} size={13} />
        </div>
      </div>
      {review.body && (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--pa-text-muted)', lineHeight: 1.6 }}>
          {review.body}
        </p>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ProProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('about');
  const [editMode, setEditMode] = useState(false);
  const [reviewModal, setReviewModal] = useState(false);

  const isOwnProfile = !id || (profile && profile.user_id === user?.id);
  const canReview = !isOwnProfile && (user?.role === 'owner' || user?.role === 'agent');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = id
        ? await marketplaceAPI.getProDetail(id)
        : await marketplaceAPI.getMyProProfile();
      setProfile(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load profile');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  function handleJobVisibilityChange(updatedBooking) {
    setProfile((p) => ({
      ...p,
      completed_jobs: p.completed_jobs.map((j) =>
        j.id === updatedBooking.id
          ? { ...j, show_on_profile: updatedBooking.show_on_profile, mask_details: updatedBooking.mask_details }
          : j
      ),
    }));
  }

  function handleProfileSaved(updatedProfile) {
    setProfile((p) => ({ ...p, ...updatedProfile }));
    setEditMode(false);
  }

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: 300 }}>
        <div style={{ color: 'var(--pa-text-subtle)', fontSize: 14 }}>Loading profile…</div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 15, color: 'var(--pa-negative)', marginBottom: 16 }}>
          {error || 'Profile not found'}
        </div>
        <button className="btn ghost" onClick={() => navigate(-1)}>Go back</button>
      </div>
    );
  }

  const initials = profile.business_name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const avgRating = profile.reviews.length > 0
    ? (profile.reviews.reduce((s, r) => s + r.rating, 0) / profile.reviews.length).toFixed(1)
    : profile.rating.toFixed(1);

  // Rating distribution
  const dist = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: profile.reviews.filter((r) => r.rating === star).length,
  }));
  const maxDist = Math.max(...dist.map((d) => d.count), 1);

  const visibleJobs = isOwnProfile
    ? profile.completed_jobs
    : profile.completed_jobs.filter((j) => j.show_on_profile);

  const profileIsIncomplete = isOwnProfile && (!profile.bio || !profile.suburb || !profile.rate_band);

  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '24px 20px' }}>

      {/* Incomplete profile banner */}
      {profileIsIncomplete && (
        <div style={{
          marginBottom: 16, padding: '12px 18px',
          background: 'var(--pa-caution-bg)',
          border: '1px solid var(--pa-caution)',
          borderRadius: 'var(--pa-radius-lg)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, flexWrap: 'wrap',
        }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--pa-caution)' }}>
              Your profile is incomplete
            </span>
            <span style={{ fontSize: 12, color: 'var(--pa-text-muted)', marginLeft: 8 }}>
              Add your bio, location and rate band to appear in client searches.
            </span>
          </div>
          <button
            className="btn primary sm"
            onClick={() => setEditMode(true)}
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Complete profile
          </button>
        </div>
      )}

      {/* Back link */}
      <button
        className="btn ghost sm"
        onClick={() => navigate(-1)}
        style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}
      >
        ← Back
      </button>

      {/* ── Profile Header ── */}
      <div data-pa-surface="dark" style={{
        background: 'linear-gradient(135deg, var(--pa-navy) 0%, var(--pa-navy-2) 60%, var(--pa-signal-deep) 130%)',
        borderRadius: 'var(--pa-radius-xl)',
        padding: '28px 28px 24px',
        color: 'var(--pa-paper)',
        marginBottom: 24,
        position: 'relative',
        boxShadow: 'var(--pa-shadow-2)',
      }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Avatar */}
          <div style={{
            width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
            background: 'rgba(255,255,255,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, fontWeight: 700,
            color: 'var(--pa-paper)',
            border: '1px solid rgba(255,255,255,0.24)',
            fontFamily: 'var(--pa-font-display)',
          }}>
            {initials}
          </div>

          {/* Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 className="serif" style={{ margin: 0, fontSize: 26, fontWeight: 600, color: 'var(--pa-paper)', letterSpacing: '-0.015em' }}>
                {profile.business_name}
              </h1>
              {profile.is_atlas_verified && (
                <span style={{
                  fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
                  padding: '3px 8px', borderRadius: 'var(--pa-radius-pill)',
                  background: 'var(--pa-signal)', color: 'var(--pa-paper)',
                }}>
                  VERIFIED
                </span>
              )}
              {!profile.is_available && (
                <span style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 'var(--pa-radius-pill)',
                  background: 'rgba(255,255,255,0.12)', color: 'var(--pa-paper)',
                }}>
                  Unavailable
                </span>
              )}
            </div>
            <div style={{ fontSize: 14, color: 'rgba(252,252,252,0.78)', marginTop: 4 }}>
              {profile.trade}{profile.secondary_trade ? ` · ${profile.secondary_trade}` : ''}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Stars rating={parseFloat(avgRating)} size={14} />
                <strong style={{ fontSize: 14, fontFamily: 'var(--pa-font-mono)' }} className="tnum">{avgRating}</strong>
                <span style={{ fontSize: 12, color: 'rgba(252,252,252,0.6)' }}>
                  ({profile.review_count} review{profile.review_count !== 1 ? 's' : ''})
                </span>
              </div>
              {profile.rate_band && (
                <span style={{ fontSize: 13, color: 'rgba(252,252,252,0.72)' }}>{profile.rate_band}</span>
              )}
              {profile.response_time && (
                <span style={{ fontSize: 12, color: 'rgba(252,252,252,0.6)' }}>
                  Responds {profile.response_time}
                </span>
              )}
              <span style={{ fontSize: 12, color: 'rgba(252,252,252,0.6)' }}>
                {profile.atlas_jobs_completed} job{profile.atlas_jobs_completed !== 1 ? 's' : ''} on PropAtlas
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
            {isOwnProfile ? (
              <button
                onClick={() => { setEditMode((e) => !e); setTab('about'); }}
                style={{
                  background: 'rgba(255,255,255,0.14)',
                  color: 'var(--pa-paper)',
                  border: '1px solid rgba(255,255,255,0.24)',
                  padding: '8px 16px',
                  borderRadius: 'var(--pa-radius-md)',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: 13,
                  fontFamily: 'var(--pa-font-ui)',
                }}
              >
                {editMode ? '✕ Cancel edit' : '✎ Edit profile'}
              </button>
            ) : (
              <>
                {canReview && (
                  <button
                    onClick={() => setReviewModal(true)}
                    style={{
                      background: 'var(--pa-signal)',
                      color: 'var(--pa-paper)',
                      border: '1px solid var(--pa-signal)',
                      padding: '8px 16px',
                      borderRadius: 'var(--pa-radius-md)',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 13,
                      fontFamily: 'var(--pa-font-ui)',
                      boxShadow: 'var(--pa-shadow-signal)',
                    }}
                  >
                    ★ Write review
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Edit Form (inline, above tabs) ── */}
      {editMode && (
        <EditProfileForm
          profile={profile}
          onSave={handleProfileSaved}
          onCancel={() => setEditMode(false)}
        />
      )}

      {/* ── Tabs ── */}
      {!editMode && (
        <>
          <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--pa-border)', marginBottom: 24 }}>
            {[
              { key: 'about', label: 'About' },
              { key: 'jobs', label: `Jobs (${visibleJobs.length})` },
              { key: 'reviews', label: `Reviews (${profile.reviews.length})` },
            ].map(({ key, label }) => {
              const active = tab === key;
              return (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  style={{
                    padding: '10px 18px',
                    fontSize: 14,
                    fontWeight: active ? 600 : 400,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: active ? 'var(--pa-navy)' : 'var(--pa-text-muted)',
                    borderBottom: active ? '2px solid var(--pa-signal)' : '2px solid transparent',
                    marginBottom: -1,
                    fontFamily: 'var(--pa-font-ui)',
                    transition: 'color var(--pa-duration-1) var(--pa-ease)',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* ── About Tab ── */}
          {tab === 'about' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
              {profile.bio && (
                <div className="panel" style={{ gridColumn: '1 / -1', padding: 20 }}>
                  <div className="eyebrow" style={{ marginBottom: 10 }}>About</div>
                  <p style={{ margin: 0, fontSize: 14, color: 'var(--pa-text)', lineHeight: 1.7 }}>
                    {profile.bio}
                  </p>
                </div>
              )}

              {profile.license_number && (
                <InfoCard label="Licence / Registration">
                  <span className="tnum" style={{ fontFamily: 'var(--pa-font-mono)', fontSize: 13 }}>
                    {profile.license_number}
                  </span>
                </InfoCard>
              )}

              {profile.service_areas?.length > 0 && (
                <InfoCard label="Service areas">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {profile.service_areas.map((area) => (
                      <span key={area} style={{
                        padding: '3px 10px',
                        background: 'var(--pa-tint)',
                        color: 'var(--pa-navy)',
                        borderRadius: 'var(--pa-radius-pill)',
                        fontSize: 12,
                      }}>
                        {area}
                      </span>
                    ))}
                  </div>
                </InfoCard>
              )}

              {profile.badges?.length > 0 && (
                <InfoCard label="Badges">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {profile.badges.map((b) => <Badge key={b} text={b} tone="success" />)}
                  </div>
                </InfoCard>
              )}

              {(profile.rate_band || profile.response_time) && (
                <InfoCard label="Service details">
                  {profile.rate_band && (
                    <div style={{ fontSize: 13, color: 'var(--pa-text)' }}>
                      Rate: <strong style={{ color: 'var(--pa-navy)' }}>{profile.rate_band}</strong>
                    </div>
                  )}
                  {profile.response_time && (
                    <div style={{ fontSize: 13, color: 'var(--pa-text)', marginTop: 4 }}>
                      Response time: {profile.response_time}
                    </div>
                  )}
                </InfoCard>
              )}

              {(!profile.bio && !profile.license_number && !profile.service_areas?.length) && (
                <div style={{
                  gridColumn: '1 / -1', textAlign: 'center',
                  padding: '40px 0', color: 'var(--pa-text-subtle)', fontSize: 14,
                }}>
                  {isOwnProfile ? 'Add details about your business using "Edit profile" above.' : 'No additional details available.'}
                </div>
              )}
            </div>
          )}

          {/* ── Jobs Tab ── */}
          {tab === 'jobs' && (
            <div>
              {isOwnProfile && (
                <div style={{
                  padding: '10px 14px',
                  background: 'var(--pa-positive-bg)',
                  border: '1px solid var(--pa-positive)',
                  borderRadius: 'var(--pa-radius-lg)',
                  fontSize: 13, color: 'var(--pa-positive)',
                  marginBottom: 20,
                }}>
                  Showing all your completed jobs. Use the controls on each card to choose what clients see on your public profile.
                </div>
              )}

              {visibleJobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px 0', color: 'var(--pa-text-subtle)', fontSize: 14 }}>
                  {isOwnProfile ? 'Completed jobs will appear here once marked done in Bookings.' : 'No completed jobs to show.'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {visibleJobs.map((job) => (
                    <JobCard
                      key={job.id}
                      job={job}
                      isOwn={isOwnProfile}
                      onVisibilityChange={handleJobVisibilityChange}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Reviews Tab ── */}
          {tab === 'reviews' && (
            <div>
              {profile.reviews.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 16,
                  marginBottom: 28,
                }}>
                  {/* Star summary */}
                  <div className="panel" style={{ padding: 20, textAlign: 'center' }}>
                    <div className="serif tnum" style={{
                      fontSize: 48, fontWeight: 600, color: 'var(--pa-navy)', lineHeight: 1,
                      fontFamily: 'var(--pa-font-display)',
                    }}>
                      {avgRating}
                    </div>
                    <div style={{ margin: '8px 0 4px' }}><Stars rating={parseFloat(avgRating)} size={16} /></div>
                    <div style={{ fontSize: 12, color: 'var(--pa-text-subtle)' }}>
                      {profile.reviews.length} review{profile.reviews.length !== 1 ? 's' : ''}
                    </div>
                  </div>

                  {/* Distribution bars */}
                  <div className="panel" style={{
                    padding: 20, display: 'flex', flexDirection: 'column',
                    justifyContent: 'center', gap: 8, minWidth: 0,
                  }}>
                    {dist.map(({ star, count }) => (
                      <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 12, color: 'var(--pa-text-muted)', width: 14, textAlign: 'right' }}>
                          {star}
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--pa-caution)' }}>★</span>
                        <div style={{ flex: 1, height: 8, background: 'var(--pa-tint)', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: 4,
                            background: 'var(--pa-signal)',
                            width: `${Math.round((count / maxDist) * 100)}%`,
                            transition: 'width var(--pa-duration-2) var(--pa-ease)',
                          }} />
                        </div>
                        <span style={{ fontSize: 12, color: 'var(--pa-text-subtle)', width: 18, fontFamily: 'var(--pa-font-mono)' }} className="tnum">
                          {count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {canReview && (
                <div style={{ marginBottom: 20 }}>
                  <button className="btn primary" onClick={() => setReviewModal(true)}>
                    ★ Write a review
                  </button>
                </div>
              )}

              {profile.reviews.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px 0', color: 'var(--pa-text-subtle)', fontSize: 14 }}>
                  No reviews yet.{canReview ? ' Be the first to leave a review.' : ''}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {profile.reviews.map((r) => <ReviewCard key={r.id} review={r} />)}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {reviewModal && (
        <ReviewModal
          proId={profile.id}
          onClose={() => setReviewModal(false)}
          onSubmitted={load}
        />
      )}
    </div>
  );
}

function InfoCard({ label, children }) {
  return (
    <div className="panel" style={{ padding: 18 }}>
      <div className="eyebrow" style={{ marginBottom: 10 }}>
        {label}
      </div>
      {children}
    </div>
  );
}
